// SETTINGS V2 — Firma (Company) page (SETTINGS_V2_SP3 §5/§6). Profil-firmy card
// + Features/MIDAS card + Kontakty card (CompanyContactsCardV2) + contact dialog.
//
// TWO base URLs (BINDING §2): company + contacts via the Customers proxy
// (getCustomer / getContacts); the MIDAS toggle READ is a raw fetch
// (chatBotApi.checkBotAccess) and WRITE the chatBotSlice thunk — both on the
// Account service. Both paths preserved verbatim.
//
// §6.3 FIX (flagged): backend serializes AddressViewModel.TypeCode as a scalar
// int (verified 2026-07-19 — plain enum + bare AddControllers(), no enum
// converter). The TS Address type says {id,name} — mistyped; correction deferred
// to post-flip cleanup. The runtime-tolerant normalizer accepts both shapes
// (legal-preferred → billing fallback) → the office-address row renders for the
// first time (it was a confirmed dead row).
//
// Full 28-prop Customer read model retained untrimmed (§2); page ROOT carries NO
// animation (a root transform would trap the fixed ActionToast — SP2 §2 rule).
// Inline styles + scoped CSS-var tokens + v2 atoms; no MUI/sx/dt/Tailwind.
// SEC-REQ-2: bot fetch + both get* dispatches in mount effects only. SEC-REQ-8:
// the only navigation is /companies/{customerId} (an id legacy already exposes).
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import ActionToast from "../../../../shared/components/ActionToast";
import Loader from "../../../../shared/components/Loader";
import { NotificationMessage } from "../../../../shared/models/models";
import { AddressTypeCode, formatAddressString } from "../../../shared/models/customerModels";
import { getCustomer } from "../../customerSettingsSlice";
import { getContacts, setSuccess as setContactSuccess, setError as setContactError } from "../../customerContactsSlice";
import chatBotApi from "../../../../shared/chatBot/ChatBotApi";
import { BotAccessResponse } from "../../../../shared/chatBot/models/chatBotModels";
import { updateBotChatSettings } from "../../../../shared/chatBot/chatBotSlice";
import { SCard, SToggle, SErrorCallout } from "../atoms";
import CompanyContactsCardV2 from "./CompanyContactsCardV2";
import ContactDialogV2 from "../dialogs/ContactDialogV2";

const ellipsis: React.CSSProperties = {
  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
};

// InfoRow — LOCAL to this page (designer lines 65-72; only this page uses it).
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    style={{
      display: "flex", gap: 12, alignItems: "baseline",
      borderTop: "1px solid var(--border-soft)", padding: "9px 0",
    }}
  >
    <div style={{ fontSize: 12, color: "var(--muted)", width: 140, flexShrink: 0 }}>{label}</div>
    <div
      style={{
        fontSize: 13, fontWeight: 500, color: "var(--ink)",
        fontFamily: "var(--font-mono)", minWidth: 0, ...ellipsis,
      }}
    >
      {value}
    </div>
  </div>
);

// §6.3 normalizer — accept scalar int OR the mistyped {id,name} shape.
const addressTypeId = (typeCode: unknown): number | undefined =>
  typeof typeCode === "number" ? typeCode : (typeCode as { id?: number } | null | undefined)?.id;

const CompanyPageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate: t } = useTranslation("settings");

  const { customer, loading, error } = useAppSelector((s) => s.customerSettings);
  const { success: contactSuccess, error: contactError } = useAppSelector(
    (s) => s.customerContacts
  );

  // MIDAS bot toggle — LOCAL state (NOT Redux), read via raw fetch (§6.1).
  const [botSettings, setBotSettings] = useState<BotAccessResponse | null>(null);
  const [botLoading, setBotLoading] = useState(true);
  const [botToggling, setBotToggling] = useState(false);

  // Page mount effect (parity, legacy lines 131-134): both dispatches.
  useEffect(() => {
    dispatch(getCustomer());
    dispatch(getContacts());
  }, [dispatch]);

  // Bot read — raw fetch, bypasses the global 403 handler (§6.1).
  useEffect(() => {
    let cancelled = false;
    setBotLoading(true);
    chatBotApi
      .checkBotAccess()
      .then((result) => {
        if (!cancelled) {
          setBotSettings(result);
          setBotLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBotSettings(null);
          setBotLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Bot write — chatBotSlice thunk; 403 / failure → SILENT revert (§6.2).
  const handleBotToggle = useCallback(async () => {
    if (!botSettings || botToggling) return;
    const newEnabled = !botSettings.enabled;
    setBotToggling(true);
    try {
      const result = await dispatch(updateBotChatSettings(newEnabled)).unwrap();
      setBotSettings(result); // server response is authoritative
    } catch {
      // 403 / failure → SILENT revert: botSettings untouched, no toast (parity)
    } finally {
      setBotToggling(false);
    }
  }, [botSettings, botToggling, dispatch]);

  // §6.3 office address — legal-preferred, billing fallback (two-pass find).
  const officeAddress =
    customer?.addresses?.find((a) => addressTypeId(a.typeCode) === AddressTypeCode.legal) ??
    customer?.addresses?.find((a) => addressTypeId(a.typeCode) === AddressTypeCode.billing);
  const addressString = officeAddress ? formatAddressString(officeAddress) : undefined;

  const profilBody =
    loading && !customer ? (
      <Loader />
    ) : !customer && error ? (
      <SErrorCallout
        title={t("settings:company.loadFailed")}
        actionLabel={t("settings:company.retry")}
        onAction={() => dispatch(getCustomer())}
      />
    ) : customer ? (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          {customer.logo?.content ? (
            <img
              src={customer.logo.content}
              alt={customer.name}
              style={{
                width: 52, height: 52, borderRadius: 10, objectFit: "cover",
                border: "1px solid var(--border)", flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: 52, height: 52, borderRadius: 10, background: "var(--bg-soft)",
                border: "1px solid var(--border)", display: "grid", placeItems: "center",
                fontSize: 18, fontWeight: 700, color: "var(--mod-accent)", flexShrink: 0,
              }}
            >
              {(customer.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 16, fontWeight: 700, color: "var(--ink)",
                letterSpacing: "-0.01em", ...ellipsis,
              }}
            >
              {customer.name}
            </div>
            {customer.registrationNumber && (
              <div
                style={{
                  fontFamily: "var(--font-mono)", fontSize: 10.5,
                  color: "var(--muted)", marginTop: 2, ...ellipsis,
                }}
              >
                {t("settings:company.registrationNumber")}: {customer.registrationNumber}
              </div>
            )}
          </div>
        </div>

        <div>
          {customer.vatId && (
            <InfoRow label={t("settings:company.vatNumber")} value={customer.vatId} />
          )}
          {customer.website && (
            <InfoRow label={t("settings:company.website")} value={customer.website} />
          )}
          {addressString && (
            <InfoRow label={t("settings:company.address")} value={addressString} />
          )}
        </div>
      </div>
    ) : null;

  return (
    // Page root: NO animation (SP2 §2 toast stacking rule).
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0, flex: 1 }}>
      {/* Contacts toasts mounted at the PAGE root (parity, legacy 172-185). */}
      <ActionToast
        open={!!contactSuccess}
        variant="success"
        message={contactSuccess || ""}
        onClose={() => dispatch(setContactSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!contactError}
        variant="error"
        message={NotificationMessage.error(contactError).message || ""}
        onClose={() => dispatch(setContactError(undefined))}
        duration={6000}
      />

      {/* ─── Card 1 — Profil firmy ─── */}
      <SCard
        title={t("settings:company.title")}
        style={{ animation: "sv2-card-reveal 0.4s ease-out both" }}
        action={
          customer ? (
            <span
              onClick={() => navigate(`/companies/${customer.id}`)}
              style={{
                fontSize: 12.5, fontWeight: 500, color: "var(--mod-accent)",
                cursor: "pointer", whiteSpace: "nowrap",
              }}
            >
              {t("settings:company.editOnNetwork")} ↗
            </span>
          ) : undefined
        }
      >
        {profilBody}
      </SCard>

      {/* ─── Card 2 — Features / MIDAS ─── */}
      <SCard
        title={t("settings:company.features")}
        style={{ animation: "sv2-card-reveal 0.4s ease-out 0.06s both" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 34, height: 34, borderRadius: 8, background: "var(--accent)",
              color: "#fff", display: "grid", placeItems: "center",
              fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 15, flexShrink: 0,
            }}
          >
            M
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
              {t("settings:company.midasTitle")}
            </div>
            <div
              style={{
                fontSize: 12.5, color: "var(--muted)", lineHeight: 1.55, marginTop: 2,
              }}
            >
              {t("settings:company.midasDescription")}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            {botLoading ? (
              <span className="sv2-skel-dot" aria-hidden="true" />
            ) : (
              <SToggle
                on={botSettings?.enabled ?? true}
                onToggle={handleBotToggle}
                disabled={botToggling || !(botSettings?.globalEnabled ?? true)}
                ariaLabel={t("settings:company.midasTitle")}
              />
            )}
          </div>
        </div>
        {botSettings && !botSettings.globalEnabled && (
          <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>
            {t("settings:company.midasGlobalDisabled")}
          </div>
        )}
      </SCard>

      {/* ─── Card 3 — Kontakty (selects its own slice state) ─── */}
      <CompanyContactsCardV2 />

      {/* Portalled contact create/edit dialog (reads its own open-state). */}
      <ContactDialogV2 />
    </div>
  );
};

export default CompanyPageV2;
